// components/ProductSelection/MediaLightbox.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
  PhotographIcon,
  ArrowsExpandIcon,
  DownloadIcon,
  PlayIcon,
  PauseIcon,
  VolumeUpIcon,
  VolumeOffIcon,
} from "@heroicons/react/outline";

const MediaLightbox = ({
  media = [],
  initialIndex = 0,
  isOpen,
  onClose,
  productName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Video controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const imageRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setIsPlaying(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, initialIndex]);

  // Reset when changing media
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setIsPlaying(false);
    setVideoProgress(0);

    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.pause();
    }
  }, [currentIndex]);

  // Video time update
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setVideoProgress((video.currentTime / video.duration) * 100);
    };

    const handleLoadedMetadata = () => {
      setVideoDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("ended", handleEnded);
    };
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          onClose();
          break;
        case " ":
          e.preventDefault();
          if (currentMedia?.type === "video") {
            togglePlay();
          }
          break;
        case "+":
        case "=":
          if (currentMedia?.type === "image") {
            handleZoomIn();
          }
          break;
        case "-":
          if (currentMedia?.type === "image") {
            handleZoomOut();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, isPlaying]);

  const currentMedia = media[currentIndex];
  const isVideo = currentMedia?.type === "video";
  const isImage = currentMedia?.type === "image";

  const handleNext = () => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleZoomIn = () => {
    if (isImage) {
      setZoom((prev) => Math.min(prev + 0.5, 5));
    }
  };

  const handleZoomOut = () => {
    if (isImage) {
      setZoom((prev) => Math.max(prev - 0.5, 1));
      if (zoom <= 1.5) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1 && isImage) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1 && isImage) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    if (isImage) {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    if (videoRef.current) {
      videoRef.current.currentTime =
        (percentage / 100) * videoRef.current.duration;
      setVideoProgress(percentage);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDownload = () => {
    if (currentMedia?.url) {
      const link = document.createElement("a");
      link.href = currentMedia.url;
      link.download = `${productName}-${currentIndex + 1}.${
        isVideo ? "mp4" : "jpg"
      }`;
      link.click();
    }
  };

  if (!isOpen || media.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="text-white">
            <h3 className="text-lg font-semibold">{productName}</h3>
            <p className="text-sm text-gray-300">
              {isVideo ? "Video" : "Image"} {currentIndex + 1} of {media.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onWheel={handleWheel}
      >
        {isImage ? (
          // Image Display
          <div
            ref={imageRef}
            className={`relative ${
              isDragging
                ? "cursor-grabbing"
                : zoom > 1
                ? "cursor-grab"
                : "cursor-default"
            }`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${
                position.y / zoom
              }px)`,
              transition: isDragging ? "none" : "transform 0.3s ease",
            }}
          >
            <img
              src={currentMedia.url}
              alt={currentMedia.altText || `Product image ${currentIndex + 1}`}
              className="max-w-[90vw] max-h-[80vh] object-contain select-none"
              draggable={false}
            />
          </div>
        ) : isVideo ? (
          // Video Display
          <div className="relative">
            <video
              ref={videoRef}
              src={currentMedia.url}
              className="max-w-[90vw] max-h-[80vh] object-contain"
              onClick={togglePlay}
              playsInline
            />

            {/* Video Overlay Controls */}
            {!isPlaying && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
              >
                <div className="bg-white/90 rounded-full p-6 group-hover:scale-110 transition-transform">
                  <PlayIcon className="w-16 h-16 text-gray-900" />
                </div>
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* Navigation Arrows */}
      {media.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all ${
              currentIndex === 0 ? "opacity-30 cursor-not-allowed" : ""
            }`}
          >
            <ChevronLeftIcon className="w-8 h-8" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === media.length - 1}
            className={`absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all ${
              currentIndex === media.length - 1
                ? "opacity-30 cursor-not-allowed"
                : ""
            }`}
          >
            <ChevronRightIcon className="w-8 h-8" />
          </button>
        </>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4">
        <div className="max-w-7xl mx-auto">
          {/* Video Progress Bar */}
          {isVideo && (
            <div className="mb-4">
              <div
                onClick={handleVideoSeek}
                className="relative h-2 bg-white/30 rounded-full cursor-pointer group"
              >
                <div
                  className="absolute h-2 bg-primary rounded-full transition-all"
                  style={{ width: `${videoProgress}%` }}
                >
                  <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"></div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-white mt-1">
                <span>{formatTime(videoRef.current?.currentTime)}</span>
                <span>{formatTime(videoDuration)}</span>
              </div>
            </div>
          )}

          {/* Thumbnails */}
          {media.length > 1 && (
            <div className="flex justify-center gap-2 mb-4 overflow-x-auto pb-2">
              {media.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? "border-white scale-110"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  {item.type === "image" ? (
                    <img
                      src={item.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <>
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <PlayIcon className="w-6 h-6 text-white" />
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-2">
            {/* Image Controls */}
            {isImage && (
              <>
                <button
                  onClick={handleZoomOut}
                  disabled={zoom <= 1}
                  className={`p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors ${
                    zoom <= 1 ? "opacity-30 cursor-not-allowed" : ""
                  }`}
                  title="Zoom Out (-)"
                >
                  <ZoomOutIcon className="w-5 h-5" />
                </button>
                <div className="px-4 py-2 bg-black/50 text-white rounded-lg font-medium min-w-[80px] text-center">
                  {Math.round(zoom * 100)}%
                </div>
                <button
                  onClick={handleZoomIn}
                  disabled={zoom >= 5}
                  className={`p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors ${
                    zoom >= 5 ? "opacity-30 cursor-not-allowed" : ""
                  }`}
                  title="Zoom In (+)"
                >
                  <ZoomInIcon className="w-5 h-5" />
                </button>
                <div className="w-px h-8 bg-white/20 mx-2"></div>
                <button
                  onClick={resetZoom}
                  className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
                  title="Reset Zoom"
                >
                  <PhotographIcon className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Video Controls */}
            {isVideo && (
              <>
                <button
                  onClick={togglePlay}
                  className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
                  title={isPlaying ? "Pause (Space)" : "Play (Space)"}
                >
                  {isPlaying ? (
                    <PauseIcon className="w-5 h-5" />
                  ) : (
                    <PlayIcon className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={toggleMute}
                  className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <VolumeOffIcon className="w-5 h-5" />
                  ) : (
                    <VolumeUpIcon className="w-5 h-5" />
                  )}
                </button>
                <div className="w-px h-8 bg-white/20 mx-2"></div>
              </>
            )}

            {/* Common Controls */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
              title="Fullscreen"
            >
              <ArrowsExpandIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
              title="Download"
            >
              <DownloadIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Keyboard Hints */}
          <div className="flex justify-center gap-4 mt-3 text-xs text-gray-400">
            <span>← → Navigate</span>
            {isImage && <span>+ - Zoom</span>}
            {isVideo && <span>Space Play/Pause</span>}
            <span>ESC Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLightbox;
